import { createSelector } from 'reselect';
import { RootState, ConsumptionState } from '../model/state';
import { ConsumptionFilter, FormattedConsumptionData, FormattedConsumptionTable, IntervalType } from 'model';
import { defaultChartData, getXAxis, buildTooltipFormatter, formatTableDate } from './helpers/consumptionDataFormat';
import moment from "moment-timezone";
import i18n from 'i18next';

/**
 * Direct selector to the state
 */
const selectConsumption = () => (state: RootState) => state.consumption;

/**
 * Other specific selectors
 */
const selectConsumptionPeriod = () => createSelector(
  selectConsumption(),
  (consumption: ConsumptionState) => consumption.selectedPeriod
);

const selectConsumptionIntervalType = () => createSelector(
  selectConsumption(),
  (consumption: ConsumptionState) => consumption.intervalType
);

const selectFormattedChartData = () => createSelector(
  selectConsumption(),
  selectConsumptionIntervalType(),
  (consumption: ConsumptionState, intervalType: IntervalType): FormattedConsumptionData => {
    if (consumption.data.items.length < 1) {
      return defaultChartData;
    }

    return {
      values: consumption.data.items.map(datum => datum.value),
      xAxis: getXAxis(intervalType, consumption.data.items, consumption.data.metadata.timezone),
      unitSystem: consumption.data.metadata.long,
      formatter: buildTooltipFormatter(intervalType, consumption.data.metadata.unit, consumption.data.items[0].time, consumption.data.metadata.timezone),
    }
  }
);

const selectConsumptionFilters = () => createSelector(
  selectConsumption(),
  (consumption: ConsumptionState) => consumption.filters
);

const selectRefreshButtonDisabled = () => createSelector(
  selectConsumptionFilters(),
  (filters: ConsumptionFilter) => {
    return !(filters.dateRange.to && filters.dateRange.from);
  }
);

const selectConsumptionTableData = () => createSelector(
  selectConsumption(),
  selectConsumptionIntervalType(),
  selectConsumptionFilters(),
  (consumption: ConsumptionState, intervalType: IntervalType, filters: ConsumptionFilter): FormattedConsumptionTable => {
    const total = consumption.data.items.reduce((acc, curr) => {
        return acc + curr.value;
    }, 0);

    return {
      columns: [
        {id: 'date', title: i18n.t('Date/Time')},
        {id: 'value', title: i18n.t('consumption')},
      ],
      data: [
        ...(consumption.data.items.map(item => ({
          date: moment(item.time).tz(consumption.data.metadata.timezone).format(formatTableDate(intervalType)),
          value: `${item.value} ${consumption.data.metadata.short}`
        }))),
        {
          date: i18n.t('Total'),
          value: `${(Math.round(total * 10) / 10)} ${consumption.data.metadata.short}`,
        }
      ],
      download: [
        [i18n.t('water-usage')],
        [],
        [i18n.t('date-from'), moment(filters.dateRange.from).tz(consumption.data.metadata.timezone).format('YYYY/MM/DD')],
        [i18n.t('date-to'), moment(filters.dateRange.to).tz(consumption.data.metadata.timezone).format('YYYY/MM/DD')],
        [i18n.t('locations'), ...(filters.locations.length ? filters.locations.map(l => l.nickname) : [i18n.t('all-locations')])],
        [i18n.t('unit-system'), consumption.data.metadata.long],
        [],
        [i18n.t('Date/Time'), i18n.t('consumption')],
        ...consumption.data.items.map(item => [
          moment(item.time).tz(consumption.data.metadata.timezone).format(formatTableDate(intervalType, true)),
          item.value.toString()
        ]),
        [
          i18n.t('Total'),
          (Math.round(total * 10) / 10).toString(),
        ]
      ]
    }
  }
);

const selectIsLoadingChart = () => createSelector(
  selectConsumption(),
  (consumption: ConsumptionState) => consumption.isLoading
);

const selectTimezone = () => createSelector(
  selectConsumption(),
  (consumption: ConsumptionState) => consumption.data.metadata.timezone
);

export default selectConsumption;
export {
  selectConsumptionPeriod,
  selectFormattedChartData,
  selectConsumptionFilters,
  selectRefreshButtonDisabled,
  selectConsumptionTableData,
  selectIsLoadingChart,
  selectTimezone,
}
